import { Link } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import { translations } from '../data/translations'
import { motion, AnimatePresence } from 'framer-motion'
import { useState } from 'react'

const Services = () => {
  const { language } = useLanguage()
  const t = translations[language]
  const [selectedService, setSelectedService] = useState(null)

  const services = [
    {
      id: 'same-day-delivery',
      icon: '🚀',
      title: language === 'en' ? 'Same-Day Delivery' : 'توصيل في نفس اليوم',
      shortDescription: language === 'en'
        ? 'Urgent deliveries completed within hours'
        : 'التوصيلات العاجلة مكتملة في غضون ساعات',
      description: language === 'en'
        ? 'Urgent deliveries completed within hours. Perfect for time-sensitive documents, food orders, and emergency shipments.'
        : 'التوصيلات العاجلة مكتملة في غضون ساعات. مثالي للمستندات الحساسة للوقت وطلبات الطعام والشحنات الطارئة.',
      features: language === 'en'
        ? [
            'Express service within 2-4 hours',
            'Real-time GPS tracking',
            'Priority handling',
            'SMS and email notifications',
            'Photo proof of delivery',
          ]
        : [
            'خدمة سريعة خلال 2-4 ساعات',
            'تتبع GPS في الوقت الفعلي',
            'معالجة أولوية',
            'إشعارات SMS والبريد الإلكتروني',
            'إثبات الصورة للتوصيل',
          ],
      details: language === 'en'
        ? 'Our same-day delivery service ensures your urgent packages reach their destination within hours. Perfect for time-sensitive documents, food orders, medical supplies, and emergency shipments. We prioritize these deliveries and provide real-time tracking updates so you can monitor your package every step of the way.'
        : 'تضمن خدمة التوصيل في نفس اليوم وصول طرودك العاجلة إلى وجهتها في غضون ساعات. مثالي للمستندات الحساسة للوقت وطلبات الطعام والإمدادات الطبية والشحنات الطارئة. نعطي أولوية لهذه التوصيلات ونوفر تحديثات تتبع في الوقت الفعلي حتى تتمكن من مراقبة طردك في كل خطوة.',
      pricing: language === 'en'
        ? 'Starting from AED 25 for same-day delivery within the same city. Express service (2-4 hours) available at AED 35.'
        : 'بدءًا من 25 درهم إماراتي للتوصيل في نفس اليوم داخل نفس المدينة. خدمة سريعة (2-4 ساعات) متاحة بسعر 35 درهم إماراتي.',
    },
    {
      id: 'scheduled-delivery',
      icon: '📅',
      title: language === 'en' ? 'Scheduled Delivery' : 'توصيل مجدول',
      shortDescription: language === 'en'
        ? 'Plan your deliveries in advance'
        : 'خطط لتوصيلاتك مسبقًا',
      description: language === 'en'
        ? 'Plan your deliveries in advance. Set recurring routes for regular shipments and optimize your logistics.'
        : 'خطط لتوصيلاتك مسبقًا. حدد مسارات متكررة للشحنات المنتظمة وحدّث لوجستياتك.',
      features: language === 'en'
        ? [
            'Flexible scheduling options',
            'Recurring route setup',
            'Cost optimization',
            'Bulk delivery discounts',
            'Dashboard management',
          ]
        : [
            'خيارات جدولة مرنة',
            'إعداد مسار متكرر',
            'تحسين التكلفة',
            'خصومات التوصيل بالجملة',
            'إدارة لوحة التحكم',
          ],
      details: language === 'en'
        ? 'Schedule your deliveries in advance to optimize your logistics and reduce costs. Set up recurring routes for regular shipments, choose specific time slots, and manage all your scheduled deliveries from one dashboard. Perfect for businesses with regular delivery needs.'
        : 'قم بجدولة توصيلاتك مسبقًا لتحسين لوجستياتك وتقليل التكاليف. قم بإعداد مسارات متكررة للشحنات المنتظمة واختر فترات زمنية محددة وأدر جميع التوصيلات المجدولة من لوحة تحكم واحدة. مثالي للشركات التي لديها احتياجات توصيل منتظمة.',
      pricing: language === 'en'
        ? 'Standard scheduled delivery: AED 15-20. Recurring routes: Up to 30% discount on bulk orders.'
        : 'التوصيل المجدول القياسي: 15-20 درهم إماراتي. المسارات المتكررة: خصم يصل إلى 30% على الطلبات بالجملة.',
    },
    {
      id: 'ecommerce-fulfillment',
      icon: '📦',
      title: language === 'en' ? 'E-Commerce Fulfillment' : 'تنفيذ التجارة الإلكترونية',
      shortDescription: language === 'en'
        ? 'End-to-end fulfillment for online stores'
        : 'تنفيذ شامل للمتاجر عبر الإنترنت',
      description: language === 'en'
        ? 'End-to-end fulfillment for online stores. Integration with major e-commerce platforms and custom solutions.'
        : 'تنفيذ شامل للمتاجر عبر الإنترنت. التكامل مع منصات التجارة الإلكترونية الرئيسية والحلول المخصصة.',
      features: language === 'en'
        ? [
            'Platform integration (Shopify, WooCommerce, Magento)',
            'Automated order processing',
            'Real-time inventory sync',
            'Custom packaging options',
            'API access',
          ]
        : [
            'تكامل المنصة (Shopify, WooCommerce, Magento)',
            'معالجة الطلبات الآلية',
            'مزامنة المخزون في الوقت الفعلي',
            'خيارات التغليف المخصصة',
            'الوصول إلى API',
          ],
      details: language === 'en'
        ? 'Complete e-commerce fulfillment solution with seamless integration to major platforms like Shopify, WooCommerce, and Magento. We handle order processing, inventory management, packaging, and delivery. Our API allows real-time inventory sync and automated order processing.'
        : 'حل تنفيذ شامل للتجارة الإلكترونية مع تكامل سلس مع المنصات الرئيسية مثل Shopify وWooCommerce وMagento. نتعامل مع معالجة الطلبات وإدارة المخزون والتغليف والتوصيل. يسمح API الخاص بنا بمزامنة المخزون في الوقت الفعلي ومعالجة الطلبات الآلية.',
      pricing: language === 'en'
        ? 'Custom pricing based on volume. Starting from AED 12 per order for high-volume clients. Contact us for a quote.'
        : 'تسعير مخصص بناءً على الحجم. بدءًا من 12 درهم إماراتي لكل طلب للعملاء ذوي الحجم الكبير. اتصل بنا للحصول على عرض أسعار.',
    },
    {
      id: 'document-delivery',
      icon: '📄',
      title: language === 'en' ? 'Document Delivery' : 'توصيل المستندات',
      shortDescription: language === 'en'
        ? 'Fast and secure document delivery'
        : 'توصيل سريع وآمن للمستندات',
      description: language === 'en'
        ? 'Fast and secure document delivery. Confidential handling for legal, financial, and corporate documents.'
        : 'توصيل سريع وآمن للمستندات. معالجة سرية للمستندات القانونية والمالية والشركات.',
      features: language === 'en'
        ? [
            'Confidential handling',
            'Sealed packaging',
            'Chain of custody tracking',
            'Signature verification',
            'Legal document support',
          ]
        : [
            'معالجة سرية',
            'تغليف مختوم',
            'تتبع سلسلة الحراسة',
            'التحقق من التوقيع',
            'دعم المستندات القانونية',
          ],
      details: language === 'en'
        ? 'Specialized secure document delivery service for legal, financial, and corporate documents. All documents are handled with strict confidentiality protocols, sealed packaging, and chain of custody tracking. We provide detailed proof of delivery including recipient signatures and timestamps.'
        : 'خدمة توصيل مستندات آمنة متخصصة للمستندات القانونية والمالية والشركات. يتم التعامل مع جميع المستندات ببروتوكولات سرية صارمة وتغليف مختوم وتتبع سلسلة الحراسة. نقدم دليلاً مفصلاً على التسليم بما في ذلك توقيعات المستلم والطوابع الزمنية.',
      pricing: language === 'en'
        ? 'Standard document delivery: AED 20. Express service: AED 30. Bulk discounts available.'
        : 'توصيل المستندات القياسي: 20 درهم إماراتي. الخدمة السريعة: 30 درهم إماراتي. خصومات بالجملة متاحة.',
    },
    {
      id: 'bookings-returns',
      icon: '📝',
      title: language === 'en' ? 'Easy Bookings & Returns' : 'حجوزات سهلة وإرجاع',
      shortDescription: language === 'en'
        ? 'Simple booking system and hassle-free returns'
        : 'نظام حجز بسيط وإرجاع خالٍ من المتاعب',
      description: language === 'en'
        ? 'Simple and intuitive booking system for all your delivery needs. Hassle-free returns process with quick pickup and processing.'
        : 'نظام حجز بسيط وبديهي لجميع احتياجات التوصيل الخاصة بك. عملية إرجاع خالية من المتاعب مع استلام ومعالجة سريعة.',
      features: language === 'en'
        ? [
            'Online booking portal',
            'Mobile app booking',
            'Phone booking support',
            'Same-day return pickup',
            'Return status tracking',
          ]
        : [
            'بوابة الحجز عبر الإنترنت',
            'حجز التطبيق المحمول',
            'دعم الحجز عبر الهاتف',
            'استلام الإرجاع في نفس اليوم',
            'تتبع حالة الإرجاع',
          ],
      details: language === 'en'
        ? 'Our user-friendly booking system makes it easy to schedule pickups and deliveries. Book online, via phone, or through our mobile app. Returns are processed quickly with same-day pickup available. Track your return status in real-time and receive notifications at every step.'
        : 'يجعل نظام الحجز سهل الاستخدام لدينا من السهل جدولة عمليات الاستلام والتوصيل. احجز عبر الإنترنت أو عبر الهاتف أو من خلال تطبيقنا المحمول. يتم معالجة الإرجاعات بسرعة مع توفر الاستلام في نفس اليوم. تتبع حالة الإرجاع الخاصة بك في الوقت الفعلي وتلقي إشعارات في كل خطوة.',
      pricing: language === 'en'
        ? 'Booking is free. Return pickup: AED 15. Processing fee: AED 5 per return.'
        : 'الحجز مجاني. استلام الإرجاع: 15 درهم إماراتي. رسوم المعالجة: 5 دراهم إماراتية لكل إرجاع.',
    },
    {
      id: 'order-tracking',
      icon: '🔍',
      title: language === 'en' ? 'Order Tracking' : 'تتبع الطلبات',
      shortDescription: language === 'en'
        ? 'Real-time tracking from pickup to delivery'
        : 'تتبع في الوقت الفعلي من الاستلام إلى التسليم',
      description: language === 'en'
        ? 'Real-time order tracking from pickup to delivery. Get instant updates and notifications at every stage of your shipment journey.'
        : 'تتبع الطلبات في الوقت الفعلي من الاستلام إلى التسليم. احصل على تحديثات وإشعارات فورية في كل مرحلة من رحلة شحنتك.',
      features: language === 'en'
        ? [
            'Real-time GPS tracking',
            'SMS notifications',
            'Email updates',
            'WhatsApp integration',
            'Delivery timeline',
          ]
        : [
            'تتبع GPS في الوقت الفعلي',
            'إشعارات SMS',
            'تحديثات البريد الإلكتروني',
            'تكامل واتساب',
            'الجدول الزمني للتوصيل',
          ],
      details: language === 'en'
        ? 'Track your orders in real-time with our advanced GPS tracking system. Receive instant SMS and email notifications at every milestone: pickup confirmation, in-transit updates, out for delivery, and delivery confirmation. Access tracking information through our website, mobile app, or WhatsApp.'
        : 'تتبع طلباتك في الوقت الفعلي مع نظام تتبع GPS المتقدم لدينا. احصل على إشعارات SMS والبريد الإلكتروني الفورية في كل معلم: تأكيد الاستلام وتحديثات العبور والتوصيل وتأكيد التسليم. الوصول إلى معلومات التتبع من خلال موقعنا الإلكتروني أو التطبيق المحمول أو واتساب.',
      pricing: language === 'en'
        ? 'Tracking is included free with all delivery services.'
        : 'التتبع مجاني مع جميع خدمات التوصيل.',
    },
    {
      id: 'secure-delivery',
      icon: '🛡️',
      title: language === 'en' ? 'Secure Delivery' : 'توصيل آمن',
      shortDescription: language === 'en'
        ? 'Professional handling with insurance options'
        : 'معالجة احترافية مع خيارات التأمين',
      description: language === 'en'
        ? 'Your packages are handled with the utmost care and security. Professional handling, insurance options, and secure delivery protocols.'
        : 'يتم التعامل مع طرودك بأقصى درجات العناية والأمان. معالجة احترافية وخيارات التأمين وبروتوكولات التوصيل الآمنة.',
      features: language === 'en'
        ? [
            'Insurance up to AED 10,000',
            'Tamper-proof packaging',
            'Signature verification',
            'Photo proof of delivery',
            'Professional handlers',
          ]
        : [
            'تأمين يصل إلى 10,000 درهم إماراتي',
            'تغليف مقاوم للعبث',
            'التحقق من التوقيع',
            'إثبات الصورة للتوصيل',
            'معالجون محترفون',
          ],
      details: language === 'en'
        ? 'Every package is handled by trained professionals following strict security protocols. We offer insurance coverage up to AED 10,000 for valuable items. All deliveries include tamper-proof packaging, signature verification, and photo proof of delivery. Our secure handling ensures your items arrive safely and intact.'
        : 'يتم التعامل مع كل طرد من قبل محترفين مدربين يتبعون بروتوكولات أمنية صارمة. نقدم تغطية تأمينية تصل إلى 10,000 درهم إماراتي للعناصر القيمة. تشمل جميع التوصيلات تغليفًا مقاومًا للعبث والتحقق من التوقيع وإثبات الصورة للتوصيل. تضمن معالجتنا الآمنة وصول عناصرك بأمان وسليمة.',
      pricing: language === 'en'
        ? 'Standard secure delivery: AED 20. Insurance: 2% of item value (minimum AED 10).'
        : 'التوصيل الآمن القياسي: 20 درهم إماراتي. التأمين: 2% من قيمة العنصر (الحد الأدنى 10 دراهم إماراتية).',
    },
    {
      id: 'cod-payment',
      icon: '💳',
      title: language === 'en' ? 'Cash & Card on Delivery' : 'نقد وبطاقة عند التسليم',
      shortDescription: language === 'en'
        ? 'Flexible payment options for customers'
        : 'خيارات دفع مرنة للعملاء',
      description: language === 'en'
        ? 'Flexible payment options for your customers. We handle payment collection securely and transfer funds promptly.'
        : 'خيارات دفع مرنة لعملائك. نحن نتعامل مع تحصيل الدفع بأمان ونحول الأموال على الفور.',
      features: language === 'en'
        ? [
            'Cash on delivery',
            'Card payment support',
            'Secure collection',
            'Fast fund transfers (24-48h)',
            'PCI-DSS compliant',
          ]
        : [
            'الدفع نقدًا عند التسليم',
            'دعم الدفع بالبطاقة',
            'تحصيل آمن',
            'تحويلات أموال سريعة (24-48 ساعة)',
            'متوافق مع PCI-DSS',
          ],
      details: language === 'en'
        ? 'Offer your customers the convenience of cash or card payment upon delivery. We accept all major credit and debit cards, and handle cash collection securely. Funds are transferred to your account within 24-48 hours. Our payment gateway is PCI-DSS compliant and provides instant payment confirmations.'
        : 'قدم لعملائك راحة الدفع نقدًا أو بالبطاقة عند التسليم. نقبل جميع بطاقات الائتمان والخصم الرئيسية ونتعامل مع تحصيل النقد بأمان. يتم تحويل الأموال إلى حسابك خلال 24-48 ساعة. بوابة الدفع لدينا متوافقة مع PCI-DSS وتوفر تأكيدات الدفع الفورية.',
      pricing: language === 'en'
        ? 'COD service fee: 2% of transaction value (minimum AED 5). Card processing: 2.5% fee.'
        : 'رسوم خدمة COD: 2% من قيمة المعاملة (الحد الأدنى 5 دراهم إماراتية). معالجة البطاقة: رسوم 2.5%.',
    },
  ]

  const openModal = (service) => {
    setSelectedService(service)
  }

  const closeModal = () => {
    setSelectedService(null)
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-primary-500 py-8 md:py-12 lg:py-16 overflow-hidden">
        {/* Grid Pattern Background */}
        <div className="absolute inset-0 z-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm font-semibold text-white/90 mb-4 uppercase tracking-wider">
              {language === 'en' ? 'Our Services' : 'خدماتنا'}
            </p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              {t.services.title}
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              {t.services.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-primary-500 mb-2 uppercase tracking-wider">
              {language === 'en' ? 'What We Offer' : 'ما نقدمه'}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'Comprehensive Delivery Solutions' : 'حلول توصيل شاملة'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group"
              >
                <div className="relative h-full bg-gradient-to-br from-white to-gray-50 rounded-3xl p-8 shadow-md hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-primary-300 overflow-hidden">
                  {/* Diagonal Accent */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary-500/10 to-transparent rounded-bl-full"></div>
                  
                  {/* Number Badge */}
                  <div className="absolute top-4 right-4 w-10 h-10 bg-primary-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </div>

                  {/* Icon */}
                  <div className="mb-6 relative z-10">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg group-hover:rotate-6 transition-transform duration-300">
                      <span className="text-3xl">{service.icon}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="relative z-10">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                      {service.shortDescription}
                    </p>
                    
                    {/* Features Preview */}
                    <div className="space-y-2 mb-6">
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                          <div className="w-1.5 h-1.5 bg-primary-500 rounded-full"></div>
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Learn More Button */}
                    <button
                      onClick={() => openModal(service)}
                      className="flex items-center text-primary-600 font-semibold text-sm hover:text-primary-700 transition-colors"
                    >
                      <span>{language === 'en' ? 'Learn More' : 'معرفة المزيد'}</span>
                      <span className="ml-2">→</span>
                    </button>
                  </div>

                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-gradient-to-r from-primary-500 to-primary-700 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              {language === 'en' ? 'Ready to Get Started?' : 'هل أنت مستعد للبدء؟'}
            </h2>
            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl mx-auto">
              {language === 'en'
                ? 'Contact us today to discuss your delivery needs and get a customized solution.'
                : 'اتصل بنا اليوم لمناقشة احتياجات التوصيل الخاصة بك والحصول على حل مخصص.'}
            </p>
            <Link
              to="/contact"
              className="inline-block px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold hover:bg-gray-100 transition-all shadow-xl hover:shadow-2xl"
            >
              {language === 'en' ? 'Contact Us →' : 'اتصل بنا →'}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {selectedService && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            />
            
            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                {/* Modal Header */}
                <div className="sticky top-0 bg-gradient-to-r from-primary-500 to-primary-600 text-white p-6 rounded-t-2xl">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                        <span className="text-4xl">{selectedService.icon}</span>
                      </div>
                      <div>
                        <h2 className="text-2xl md:text-3xl font-bold mb-1">
                          {selectedService.title}
                        </h2>
                        <p className="text-white/90 text-sm">
                          {selectedService.description}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={closeModal}
                      className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
                    >
                      <span className="text-2xl">×</span>
                    </button>
                  </div>
                </div>

                {/* Modal Body */}
                <div className="p-6 md:p-8">
                  {/* Details */}
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {language === 'en' ? 'Service Details' : 'تفاصيل الخدمة'}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {selectedService.details}
                    </p>
                  </div>

                  {/* Features */}
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      {language === 'en' ? 'Key Features' : 'الميزات الرئيسية'}
                    </h3>
                    <ul className="space-y-3">
                      {selectedService.features.map((feature, idx) => (
                        <motion.li
                          key={idx}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          className="flex items-start gap-3 text-gray-700"
                        >
                          <span className="text-primary-600 text-xl mt-1">✓</span>
                          <span className="text-base">{feature}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Pricing */}
                  <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-6 border-l-4 border-primary-500">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {language === 'en' ? 'Pricing' : 'التسعير'}
                    </h3>
                    <p className="text-gray-700 text-lg">
                      {selectedService.pricing}
                    </p>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="sticky bottom-0 bg-gray-50 p-6 rounded-b-2xl border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row gap-4 justify-end">
                    <button
                      onClick={closeModal}
                      className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                    >
                      {language === 'en' ? 'Close' : 'إغلاق'}
                    </button>
                    <Link
                      to="/contact"
                      onClick={closeModal}
                      className="px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors text-center"
                    >
                      {language === 'en' ? 'Contact Us' : 'اتصل بنا'}
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Services
